import { useRef, useCallback, useEffect } from 'react'
import styles from './RangeSlider.module.scss'

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Snap a raw value to the nearest step boundary within [min, max].
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @param {number} step
 * @returns {number}
 */
function snapToStep(value, min, max, step) {
  const snapped = Math.round((value - min) / step) * step + min
  return Math.min(max, Math.max(min, snapped))
}

/**
 * Convert an absolute value to a percentage position within [min, max].
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number} 0–100
 */
function toPercent(value, min, max) {
  if (max === min) return 0
  return ((value - min) / (max - min)) * 100
}

const DEFAULT_FORMAT = (v) => String(v)

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Dual-handle range slider primitive.
 *
 * Does NOT render a label row or value display — those belong in the caller.
 * Supports drag (mouse + touch), rail-click with smooth transition, keyboard
 * navigation, and thumb swap when handles cross.
 *
 * @param {object}   props
 * @param {[number, number]} props.value        - Controlled [lo, hi], always sorted.
 * @param {[number, number]} props.defaultValue - Global [min, max] bounds.
 * @param {number}  [props.step=1]              - Snap increment.
 * @param {(v: [number, number]) => void} props.onChange
 * @param {(v: number) => string} [props.formatValue] - Used for aria-valuetext.
 * @param {boolean} [props.isActive=false]      - Whether the filter is actively filtering.
 * @param {boolean} [props.disabled=false]
 * @param {string}  [props.id]                  - Base string for aria-labels.
 */
export function RangeSlider({
  value,
  defaultValue,
  step = 1,
  onChange,
  formatValue = DEFAULT_FORMAT,
  isActive = false,
  disabled = false,
  id,
}) {
  const [min, max] = defaultValue
  const [lo, hi] = value

  const rootRef = useRef(null)
  const railRef = useRef(null)

  // Up-to-date value snapshot — avoids stale closures inside document listeners.
  const valueRef = useRef([lo, hi])
  useEffect(() => {
    valueRef.current = [lo, hi]
  }, [lo, hi])

  // ── Pointer → value ──────────────────────────────────────────────────────

  const valueFromClientX = useCallback(
    (clientX) => {
      const rail = railRef.current
      if (!rail) return null
      const rect = rail.getBoundingClientRect()
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      return snapToStep(min + ratio * (max - min), min, max, step)
    },
    [min, max, step],
  )

  // Always emits sorted [min, max] — enables natural thumb swap on crossing.
  const emitChange = useCallback(
    (a, b) => onChange([Math.min(a, b), Math.max(a, b)]),
    [onChange],
  )

  // ── Dragging state (mutate DOM directly to avoid re-render cost) ─────────
  // Stores 'lo' | 'hi' | 'false' so CSS can show tooltip only on the active thumb.

  const setDragging = useCallback((side) => {
    if (rootRef.current) {
      rootRef.current.dataset.dragging = side ?? 'false'
    }
    // Lock cursor on the entire document so it doesn't flicker when pointer
    // moves faster than the thumb and briefly leaves the element.
    if (side) {
      document.body.style.cursor = 'grabbing'
    } else {
      document.body.style.cursor = ''
    }
  }, [])

  // ── Drag (mouse + touch) ─────────────────────────────────────────────────

  const startDrag = useCallback(
    (side) => (e) => {
      if (disabled) return
      e.preventDefault()
      e.stopPropagation()
      setDragging(side)

      // Capture the OTHER thumb's value once at drag start and keep it fixed.
      // Reading it from valueRef on every move causes both thumbs to march
      // together after a swap, because the "other" slot now holds the last
      // dragged value instead of the stationary thumb.
      const otherValue = side === 'lo' ? valueRef.current[1] : valueRef.current[0]

      const onMove = (moveE) => {
        moveE.preventDefault()
        const clientX = moveE.touches ? moveE.touches[0].clientX : moveE.clientX
        const newVal = valueFromClientX(clientX)
        if (newVal === null) return
        emitChange(newVal, otherValue)
      }

      const onUp = () => {
        setDragging(null)
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend', onUp)
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.addEventListener('touchmove', onMove, { passive: false })
      document.addEventListener('touchend', onUp)
    },
    [disabled, valueFromClientX, emitChange, setDragging],
  )

  // ── Rail click — transition enabled (data-dragging stays false) ──────────

  const handleRailPointerDown = useCallback(
    (e) => {
      if (disabled) return
      // Thumb's own handler called stopPropagation — this is a belt-and-suspenders guard.
      if (e.target.dataset.thumb) return
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const newVal = valueFromClientX(clientX)
      if (newVal === null) return
      const distLo = Math.abs(newVal - lo)
      const distHi = Math.abs(newVal - hi)
      if (distLo <= distHi) emitChange(newVal, hi)
      else emitChange(lo, newVal)
    },
    [disabled, valueFromClientX, lo, hi, emitChange],
  )

  // ── Keyboard ─────────────────────────────────────────────────────────────

  const handleKeyDown = useCallback(
    (side) => (e) => {
      if (disabled) return
      const current = side === 'lo' ? lo : hi
      const range = max - min
      // PageUp/Down = 10 % of full range, rounded to nearest step.
      const bigStep = Math.max(step, Math.round((range * 0.1) / step) * step)
      let next = current

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next = current - step
          break
        case 'ArrowRight':
        case 'ArrowUp':
          next = current + step
          break
        case 'Home':
          next = min
          break
        case 'End':
          next = max
          break
        case 'PageDown':
          next = current - bigStep
          break
        case 'PageUp':
          next = current + bigStep
          break
        default:
          return
      }

      e.preventDefault()
      next = Math.min(max, Math.max(min, next))
      if (side === 'lo') emitChange(next, hi)
      else emitChange(lo, next)
    },
    [disabled, lo, hi, min, max, step, emitChange],
  )

  // ── Derived positions ─────────────────────────────────────────────────────

  const loPercent = toPercent(lo, min, max)
  const hiPercent = toPercent(hi, min, max)
  const trackLeft = Math.min(loPercent, hiPercent)
  const trackWidth = Math.abs(hiPercent - loPercent)

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={rootRef}
      className={styles.root}
      data-dragging="false"
      data-active={isActive || undefined}
      data-disabled={disabled || undefined}
      onMouseDown={handleRailPointerDown}
      onTouchStart={handleRailPointerDown}
    >
      <div ref={railRef} className={styles.rail}>
        <div
          className={styles.track}
          style={{ left: `${trackLeft}%`, width: `${trackWidth}%` }}
        />

        {/* Lo thumb — positioned inside rail so left:0% aligns thumb center with rail left edge.
            Root padding (0.625em = half-thumb) means thumb outer edge matches the caller's
            content edge (e.g. FilterCard label). */}
        <div
          className={styles.thumb}
          data-thumb="lo"
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={lo}
          aria-valuetext={formatValue(lo)}
          aria-label={id ? `${id} minimum` : 'minimum'}
          style={{ left: `${loPercent}%` }}
          onMouseDown={startDrag('lo')}
          onTouchStart={startDrag('lo')}
          onKeyDown={handleKeyDown('lo')}
        >
          <span className={styles.tooltip} aria-hidden="true">{formatValue(lo)}</span>
        </div>

        {/* Hi thumb */}
        <div
          className={styles.thumb}
          data-thumb="hi"
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={hi}
          aria-valuetext={formatValue(hi)}
          aria-label={id ? `${id} maximum` : 'maximum'}
          style={{ left: `${hiPercent}%` }}
          onMouseDown={startDrag('hi')}
          onTouchStart={startDrag('hi')}
          onKeyDown={handleKeyDown('hi')}
        >
          <span className={styles.tooltip} aria-hidden="true">{formatValue(hi)}</span>
        </div>
      </div>
    </div>
  )
}
